import { s } from '@/theme/tokens';
import { Box, Text } from '@/ui';
import { SingleSelectFilterField } from '@/ui/composites/filters/SingleSelectFilterField';
import { NumberSpinner } from '@/ui/primitives/NumberField';
import { TextField } from '@/ui/primitives/TextField';
import { isSingularValue, toPluralUnitLabel, toSingularUnitLabel } from './utils';
import type { ReactElement } from 'react';

const thresholdFieldHeight = 32;

type ThresholdValueUnitRowProps<TUnit extends string> = {
  label: string;
  value: string;
  unit: TUnit;
  templateColumns: string;
  options: Array<{ value: TUnit; label: string }>;
  error: boolean;
  betweenText?: string;
  contextAtEnd?: boolean;
  pluralizeUnitLabelsByValue?: boolean;
  disabled?: boolean;
  useNumberField?: boolean;
  onValueChange: (next: string) => void;
  onUnitChange: (next: TUnit) => void;
};

export const ThresholdValueUnitRow = <TUnit extends string>({
  label,
  value,
  unit,
  templateColumns,
  options,
  error,
  betweenText,
  contextAtEnd = false,
  pluralizeUnitLabelsByValue = false,
  disabled = false,
  useNumberField = false,
  onValueChange,
  onUnitChange,
}: ThresholdValueUnitRowProps<TUnit>): ReactElement => {
  const selectElement = (
    <SingleSelectFilterField
      fullWidth
      disabled={disabled}
      inputAriaLabel={label}
      inputHeight={thresholdFieldHeight}
      inputBackgroundColor="background.default"
      value={unit}
      options={options.map((option) => ({
        value: option.value,
        label:
          pluralizeUnitLabelsByValue && !isSingularValue(value)
            ? toPluralUnitLabel(option.label)
            : toSingularUnitLabel(option.label),
      }))}
      onChange={(nextValue) => {
        onUnitChange(nextValue as TUnit);
      }}
    />
  );

  const contextElement = betweenText ? (
    <Text
      role="body"
      color="textSecondary"
      sx={{
        whiteSpace: 'nowrap',
        textAlign: 'center',
        justifySelf: 'center',
        fontSize: (theme) => theme.typography.subtitle1.fontSize,
        lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
      }}
    >
      {betweenText}
    </Text>
  ) : null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: templateColumns,
        alignItems: 'center',
        columnGap: s('lg'),
        width: '100%',
        '& > *': { minWidth: 0 },
      }}
    >
      <Text
        role="body"
        color="textSecondary"
        sx={{
          whiteSpace: 'nowrap',
          textAlign: 'right',
          justifySelf: 'end',
          fontSize: (theme) => theme.typography.subtitle1.fontSize,
          lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
        }}
      >
        {label}
      </Text>
      {useNumberField ? (
        <NumberSpinner
          value={value}
          min={1}
          step={1}
          size="small"
          inputHeight={thresholdFieldHeight}
          error={error}
          disabled={disabled}
          fieldStyle="settings"
          placeholder="Enter value"
          sx={[{ width: '100%' }, disabled ? { '& input': { color: 'text.disabled' } } : {}]}
          onChange={onValueChange}
        />
      ) : (
        <TextField
          label={undefined}
          placeholder="Enter value"
          type="number"
          inputHeight={thresholdFieldHeight}
          fieldStyle="settings"
          value={value}
          error={error}
          disabled={disabled}
          sx={[
            { width: '100%' },
            disabled
              ? {
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'text.disabled',
                    color: 'text.disabled',
                  },
                }
              : {},
          ]}
          onChange={(event) => {
            onValueChange(event.target.value);
          }}
        />
      )}
      {contextAtEnd ? (
        <>
          {selectElement}
          {contextElement}
        </>
      ) : (
        <>
          {contextElement}
          {selectElement}
        </>
      )}
    </Box>
  );
};
